import React, { useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { useApiAuth } from "../hooks/useApiAuth";
import { useSearch } from "../contexts/SearchContext";
import { useGlobalSearch } from "../contexts/GlobalSearchContext";
import ViewInfrastructures from "./redteam/ViewInfrastructures";
import RequestInfrastructure from "./redteam/RequestInfrastructure";
import Domains from "./redteam/Domains";
import Tenants from "./redteam/Tenants";
import Phishing from "./redteam/Phishing";
import Admin from "./redteam/Admin";
import { InfraEditSession } from "./redteam/types";
import { isInfraAdmin } from "../utils/authUtils";

export type { InfraEditSession };

const RedTeamPage: React.FC = () => {
  const {
    isApiReady,
    isLoading: authLoading,
    error: authError,
    appRoles,
  } = useApiAuth();
  const showAdmin = isInfraAdmin(appRoles);
  const { setSearchTerm } = useSearch();
  const { clearGlobalSearch } = useGlobalSearch();
  const [activeTab, setActiveTab] = useState("view");
  const [editSession, setEditSession] = useState<InfraEditSession | null>(null);

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    setSearchTerm("");
    clearGlobalSearch();
    if (tabKey !== "request") {
      setEditSession(null);
    }
  };

  const startModify = (session: InfraEditSession) => {
    setEditSession(session);
    setActiveTab("request");
    setSearchTerm("");
    clearGlobalSearch();
  };

  const clearEditSession = () => setEditSession(null);

  const redTeamTabs = [
    {
      key: "view",
      label: "View infrastructures",
      icon: "pi pi-eye",
      component: <ViewInfrastructures onModify={startModify} />,
    },
    {
      key: "request",
      label: "Request infrastructure",
      icon: "pi pi-plus-circle",
      component: (
        <RequestInfrastructure
          editSession={editSession}
          onEditConsumed={clearEditSession}
          onSubmitted={() => {
            clearEditSession();
            setActiveTab("view");
          }}
        />
      ),
    },
    {
      key: "domains",
      label: "Domains",
      icon: "pi pi-globe",
      component: <Domains />,
    },
    {
      key: "tenants",
      label: "Tenants",
      icon: "pi pi-building",
      component: <Tenants />,
    },
    {
      key: "phishing",
      label: "Phishing",
      icon: "pi pi-envelope",
      component: <Phishing />,
    },
    ...(showAdmin
      ? [
          {
            key: "admin",
            label: "Admin",
            icon: "pi pi-shield",
            component: <Admin />,
          },
        ]
      : []),
  ];

  if (!isApiReady) {
    return (
      <div className="page-container">
        <Card title="Red Team">
          <div className="flex justify-content-center">
            {authLoading ? (
              <div>Loading authentication...</div>
            ) : authError ? (
              <div className="text-red-500">
                Authentication error: {authError}
              </div>
            ) : (
              <div>Waiting for authentication...</div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  const currentTabData =
    redTeamTabs.find((tab) => tab.key === activeTab) || redTeamTabs[0];

  return (
    <div className="page-container">
      <div className="admin-nav mb-4">
        <div className="flex flex-wrap gap-2">
          {redTeamTabs.map((tab) => (
            <Button
              key={tab.key}
              label={tab.label}
              icon={tab.icon}
              className={`p-button-sm ${
                activeTab === tab.key
                  ? "p-button-primary"
                  : "p-button-outlined p-button-secondary"
              }`}
              onClick={() => handleTabChange(tab.key)}
            />
          ))}
        </div>
      </div>
      <Card>{currentTabData.component}</Card>
    </div>
  );
};

export default RedTeamPage;
